/**
 * FRONT-04: Composant de sélection des lots techniques et zones d'intervention
 * 
 * Nouveau comportement :
 * - Dropdown sélectionne UN lot → affiche ses zones
 * - Tags pour les lots déjà sélectionnés
 * - Lot 1xx → Zones régionales (checkbox régions)
 * - Lot 2xx → Zones départementales (checkbox départements)
 * - Sauvegarde immédiate via PUT /profile/lots
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './LotsSelector.css';

// Composant checkbox avec état indéterminé — déclaré hors du composant parent
// pour éviter un remount (et donc un flash) à chaque re-render du parent
const IndeterminateCheckbox: React.FC<{
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  disabled?: boolean;
}> = ({ checked, indeterminate, onChange, disabled }) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
  );
};

// Types
interface LotTechnique {
  id: number;
  nom: string;
  code?: string;
}

interface Region {
  id: number;
  nom: string;
}

interface Departement {
  id: number;
  nom: string;
  id_region?: number;
  region?: Region;
}

interface LotDepartementSelection {
  lot_technique_id: number;
  lot_technique_code?: string;
  lot_technique_nom?: string;
  departement_id: number;
  departement_nom?: string;
  region_id?: number;
  region_nom?: string;
}

// FRONT-09: Lot AC verrouille
interface LotAccordCadre {
  lot_technique_id: number;
  departement_id: number;
  rang?: number | null;
}

interface LotsSelectorProps {
  /** Lots départements déjà sélectionnés (depuis GET /profile) */
  lotsDepartements: LotDepartementSelection[];
  /** Callback appelé après chaque modification */
  onLotsChange: (lotTechniqueId: number, departements: number[]) => Promise<void>;
  /** Désactivé */
  disabled?: boolean;
  /** FRONT-09: Lots AC verrouilles (non modifiables) */
  lotsAccordCadre?: LotAccordCadre[];
}

const API_URL = process.env.REACT_APP_API_URL;

// Cache module-level : les données de référentiel sont partagées entre toutes
// les instances et persistées entre les montages/démontages du composant.
// Évite le re-fetch (et le flash du spinner) à chaque entrée en mode édition.
let referentielCache: {
  lotsTechniques: LotTechnique[];
  departements: Departement[];
  regions: Region[];
} | null = null;
let referentielPromise: Promise<void> | null = null;

const fetchReferentiel = async (): Promise<void> => {
  if (referentielCache) return;
  if (referentielPromise) return referentielPromise;

  referentielPromise = (async () => {
    const [lotsResult, deptsResult] = await Promise.all([
      fetch(`${API_URL}/lots-technique/categories`, { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
      fetch(`${API_URL}/departements`, { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
    ]);

    const lotsData: LotTechnique[] = Array.isArray(lotsResult) ? lotsResult :
      (lotsResult.data && Array.isArray(lotsResult.data)) ? lotsResult.data : [];
    const deptsData: Departement[] = deptsResult.data || [];

    const regionsMap = new Map<number, Region>();
    deptsData.forEach((dept: Departement) => {
      if (dept.region && dept.region.id && !regionsMap.has(dept.region.id)) {
        regionsMap.set(dept.region.id, dept.region);
      }
    });

    referentielCache = {
      lotsTechniques: lotsData,
      departements: deptsData,
      regions: Array.from(regionsMap.values()).sort((a, b) => a.nom.localeCompare(b.nom)),
    };
  })();

  return referentielPromise;
};

const LotsSelector: React.FC<LotsSelectorProps> = ({
  lotsDepartements,
  onLotsChange,
  disabled = false,
  lotsAccordCadre = [],
}) => {
  // États — initialisés depuis le cache si déjà disponible
  const [lotsTechniques, setLotsTechniques] = useState<LotTechnique[]>(referentielCache?.lotsTechniques ?? []);
  const [departements, setDepartements] = useState<Departement[]>(referentielCache?.departements ?? []);
  const [regions, setRegions] = useState<Region[]>(referentielCache?.regions ?? []);
  const [isLoading, setIsLoading] = useState(() => referentielCache === null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lot actuellement sélectionné dans la dropdown (pour afficher ses zones)
  const [currentLotId, setCurrentLotId] = useState<number | null>(null);

  // Charger les données de référence (depuis le cache ou l'API)
  useEffect(() => {
    if (referentielCache) return; // déjà en cache, pas de fetch
    fetchReferentiel()
      .then(() => {
        if (referentielCache) {
          setLotsTechniques(referentielCache.lotsTechniques);
          setDepartements(referentielCache.departements);
          setRegions(referentielCache.regions);
        }
      })
      .catch(() => setError('Erreur lors du chargement des données'))
      .finally(() => setIsLoading(false));
  }, []);

  // Lots techniques déjà sélectionnés (extraits des lotsDepartements)
  const selectedLotsTechniques = useMemo(() => {
    const lotsSet = new Set<number>();
    lotsDepartements.forEach(ld => {
      if (ld.lot_technique_id) lotsSet.add(ld.lot_technique_id);
    });
    return Array.from(lotsSet);
  }, [lotsDepartements]);

  // Départements sélectionnés pour le lot courant
  const currentLotDepartements = useMemo(() => {
    if (!currentLotId) return [];
    return lotsDepartements
      .filter(ld => ld.lot_technique_id === currentLotId)
      .map(ld => ld.departement_id);
  }, [lotsDepartements, currentLotId]);

  // FRONT-09: Departements AC verrouilles pour le lot courant
  const lockedDepartements = useMemo(() => {
    if (!currentLotId) return new Set<number>();
    return new Set(
      lotsAccordCadre
        .filter(lac => lac.lot_technique_id === currentLotId)
        .map(lac => lac.departement_id)
    );
  }, [lotsAccordCadre, currentLotId]);

  // FRONT-09: Verifier si un departement est verrouille (AC)
  const isDepartementLocked = useCallback((deptId: number): boolean => {
    return lockedDepartements.has(deptId);
  }, [lockedDepartements]);

  // Infos du lot courant
  const currentLot = useMemo(() => {
    return lotsTechniques.find(l => l.id === currentLotId);
  }, [lotsTechniques, currentLotId]);

  // Déterminer si un lot est régional (1xx) ou départemental (2xx)
  const isLotRegional = useCallback((lotId: number): boolean => {
    const lot = lotsTechniques.find(l => l.id === lotId);
    if (!lot || !lot.code) return false;
    const code = parseInt(lot.code);
    return code >= 100 && code < 200;
  }, [lotsTechniques]);

  // Départements par région (calculé depuis les données BDD)
  const departementsByRegion = useMemo(() => {
    const map = new Map<number, number[]>();
    departements.forEach(dept => {
      const regionId = dept.id_region || dept.region?.id;
      if (regionId) {
        if (!map.has(regionId)) map.set(regionId, []);
        map.get(regionId)!.push(dept.id);
      }
    });
    return map;
  }, [departements]);

  // Calculer l'état d'une région pour le lot courant
  const getRegionState = useCallback((regionId: number): 'checked' | 'unchecked' | 'indeterminate' => {
    const regionDepts = departementsByRegion.get(regionId) || [];
    if (regionDepts.length === 0) return 'unchecked';

    const selectedInRegion = regionDepts.filter(deptId => currentLotDepartements.includes(deptId));

    if (selectedInRegion.length === 0) return 'unchecked';
    if (selectedInRegion.length === regionDepts.length) return 'checked';
    return 'indeterminate';
  }, [departementsByRegion, currentLotDepartements]);

  // Sélectionner un lot dans la dropdown
  const handleLotSelect = (lotId: number) => {
    setCurrentLotId(lotId);
  };

  // FRONT-23: Supprimer un lot technique (et toutes ses zones PL)
  const handleRemoveLot = async (lotTechniqueId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || isSaving) return;

    // Retirer toutes les zones de ce lot (équivalent à appeler onLotsChange avec un tableau vide)
    setIsSaving(true);
    try {
      await onLotsChange(lotTechniqueId, []);

      // Si le lot supprimé était le lot courant, réinitialiser
      if (currentLotId === lotTechniqueId) {
        setCurrentLotId(null);
      }
    } catch (err) {
      console.error("Erreur suppression lot:", err);
      setError("Erreur lors de la suppression du lot");
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  // Cocher/décocher une région (lot 1xx)
  const handleRegionChange = async (regionId: number) => {
    if (disabled || isSaving || !currentLotId) return;

    const regionDepts = departementsByRegion.get(regionId) || [];
    // FRONT-09: Filtrer les departements non verrouilles
    const unlockeddepts = regionDepts.filter(deptId => !isDepartementLocked(deptId));
    if (unlockeddepts.length === 0) return; // Tous verrouilles

    const currentState = getRegionState(regionId);

    let newDepartements: number[];

    if (currentState === 'checked' || currentState === 'indeterminate') {
      // Désélectionner : retirer les départements non verrouillés de cette région
      newDepartements = currentLotDepartements.filter(deptId =>
        !unlockeddepts.includes(deptId)
      );
    } else {
      // Sélectionner : ajouter les départements non verrouillés de cette région
      newDepartements = Array.from(new Set([...currentLotDepartements, ...unlockeddepts]));
    }

    setIsSaving(true);
    try {
      await onLotsChange(currentLotId, newDepartements);
    } catch (err) {
      console.error('Erreur mise à jour région:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Cocher/décocher un département (lot 2xx)
  const handleDepartementChange = async (deptId: number) => {
    // FRONT-09: Bloquer si departement AC verrouille
    if (disabled || isSaving || !currentLotId || isDepartementLocked(deptId)) return;

    const isSelected = currentLotDepartements.includes(deptId);
    const newDepartements = isSelected
      ? currentLotDepartements.filter(id => id !== deptId)
      : [...currentLotDepartements, deptId];

    setIsSaving(true);
    try {
      await onLotsChange(currentLotId, newDepartements);
    } catch (err) {
      console.error('Erreur mise à jour département:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Tout sélectionner/désélectionner (régions)
  const handleSelectAllRegions = async () => {
    if (disabled || isSaving || !currentLotId) return;

    const allDepts = departements.map(d => d.id);
    const allSelected = allDepts.every(deptId => currentLotDepartements.includes(deptId));

    const newDepartements = allSelected ? [] : allDepts;

    setIsSaving(true);
    try {
      await onLotsChange(currentLotId, newDepartements);
    } catch (err) {
      console.error('Erreur tout sélectionner:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Tout sélectionner/désélectionner (départements)
  const handleSelectAllDepartements = async () => {
    if (disabled || isSaving || !currentLotId) return;

    const allDepts = departements.map(d => d.id);
    const allSelected = allDepts.every(deptId => currentLotDepartements.includes(deptId));

    const newDepartements = allSelected ? [] : allDepts;

    setIsSaving(true);
    try {
      await onLotsChange(currentLotId, newDepartements);
    } catch (err) {
      console.error('Erreur tout sélectionner:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="lots-selector loading">
        <div className="loading-spinner"></div>
        <p>Chargement des lots...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lots-selector error">
        <p>{error}</p>
      </div>
    );
  }

  const isCurrentLotRegional = currentLotId ? isLotRegional(currentLotId) : false;

  return (
    <div className={`lots-selector ${disabled ? 'disabled' : ''} ${isSaving ? 'saving' : ''}`}>
      {/* Section 1: Sélection du lot technique */}
      <div className="selector-header">
        <h3>Lots techniques</h3>
        <p className="selector-info">Sélectionnez les lots où vous souhaitez être référencé</p>
      </div>

      <section className="selector-section">
        <div className="lots-selection">
          {/* Dropdown de sélection */}
          <select
            value={currentLotId || ''}
            onChange={e => {
              const lotId = Number(e.target.value);
              if (lotId) handleLotSelect(lotId);
            }}
            disabled={disabled || isSaving}
            className="lot-select"
          >
            <option value="">Sélectionnez un lot technique...</option>
            {lotsTechniques.map(lot => (
              <option key={lot.id} value={lot.id}>
                {lot.code ? `${lot.code} - ` : ''}{lot.nom}
                {isLotRegional(lot.id) ? ' (régional)' : ' (départemental)'}
              </option>
            ))}
          </select>

          {/* Tags des lots déjà sélectionnés */}
          {selectedLotsTechniques.length > 0 && (
            <div className="selected-tags inscription-tags">
              {selectedLotsTechniques.map(lotId => {
                const lot = lotsTechniques.find(l => l.id === lotId);
                const isActive = lotId === currentLotId;
                const deptCount = lotsDepartements.filter(ld => ld.lot_technique_id === lotId).length;

                return lot ? (
                  <span
                    key={lotId}
                    className={`lot-tag lot-tag-clickable ${isActive ? 'lot-tag-active' : ''}`}
                    onClick={() => handleLotSelect(lotId)}
                    title={`Cliquez pour voir/modifier les zones (${deptCount} zone${deptCount > 1 ? 's' : ''})`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLotSelect(lotId)}
                  >
                    {lot.code ? `${lot.code} - ` : ''}{lot.nom}
                    <span className="zone-count">({deptCount})</span>
                    <button
                      type="button"
                      className="remove-tag"
                      onClick={(e) => handleRemoveLot(lotId, e)}
                      title="Retirer ce lot"
                      aria-label={`Retirer le lot ${lot.nom}`}
                      disabled={disabled || isSaving}
                    >
                      ×
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      </section>

      {/* Section 2: Zones selon le lot sélectionné */}
      {currentLotId && isCurrentLotRegional && (
        <div>
          <div className="selector-header">
            <h3>Zones régionales (pour lot {currentLot?.code || currentLotId})</h3>
            <p className="selector-info">
              Sélectionner une région = intervention sur tous ses départements pour les lots régionaux
            </p>
          </div>
          <section className="selector-section zones-section">

            <div className="selector-toolbar">
              <button
                type="button"
                className="select-all-btn"
                onClick={handleSelectAllRegions}
                disabled={disabled || isSaving}
              >
                {departements.every(d => currentLotDepartements.includes(d.id))
                  ? 'Tout désélectionner'
                  : 'Tout sélectionner'}
              </button>
              {/* {isSaving && <span className="saving-indicator">Enregistrement...</span>} */}
            </div>

            <div className="regions-grid">
              {regions.map(region => {
                const state = getRegionState(region.id);

                return (
                  <label
                    key={region.id}
                    className={`region-checkbox ${state === 'checked' ? 'selected' : ''} ${state === 'indeterminate' ? 'partial' : ''}`}
                  >
                    <IndeterminateCheckbox
                      checked={state === 'checked'}
                      indeterminate={state === 'indeterminate'}
                      onChange={() => handleRegionChange(region.id)}
                      disabled={disabled || isSaving}
                    />
                    <span className="region-nom">{region.nom}</span>
                  </label>
                );
              })}
            </div>

            <div className="storage-note">
              <span>Les départements de chaque region sélectionnée seront enregistrés</span>
            </div>
          </section>
        </div>

      )}

      {currentLotId && !isCurrentLotRegional && (
        <section className="selector-section zones-section">
          <div className="selector-header">
            <h3>Zones départementales (pour lot {currentLot?.code || currentLotId})</h3>
            <p className="selector-info">
              Sélection département par département pour les lots départementaux
            </p>
          </div>

          <div className="selector-toolbar">
            <button
              type="button"
              className="select-all-btn"
              onClick={handleSelectAllDepartements}
              disabled={disabled || isSaving}
            >
              {departements.every(d => currentLotDepartements.includes(d.id))
                ? 'Tout désélectionner'
                : 'Tout sélectionner'}
            </button>
            {/* {isSaving && <span className="saving-indicator">Enregistrement...</span>} */}
          </div>

          <div className="departements-grid">
            {departements.map(dept => {
              const isSelected = currentLotDepartements.includes(dept.id);
              const isLocked = isDepartementLocked(dept.id);

              return (
                <label
                  key={dept.id}
                  className={`departement-checkbox ${isSelected ? 'selected' : ''} ${isLocked ? 'locked' : ''}`}
                  title={isLocked ? 'Lot titulaire Accord Cadre (non modifiable)' : ''}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleDepartementChange(dept.id)}
                    disabled={disabled || isSaving || isLocked}
                  />
                  <span className="dept-code">{String(dept.id).padStart(2, '0')}</span>
                  <span className="dept-separator">-</span>
                  <span className="dept-nom">{dept.nom}</span>
                  {isLocked && <span className="locked-indicator">[AC]</span>}
                </label>
              );
            })}
          </div>
        </section>
      )}

      {/* Message si aucun lot sélectionné */}
      {!currentLotId && selectedLotsTechniques.length === 0 && (
        <div className="no-lots-notice">
          <p>Selectionnez un lot technique pour definir vos zones d'intervention</p>
        </div>
      )}

      {/* Message si lot sélectionné mais pas affiché */}
      {!currentLotId && selectedLotsTechniques.length > 0 && (
        <div className="select-lot-notice">
          <span className="notice-icon">👆</span>
          <p>Cliquez sur un lot ci-dessus pour voir ou modifier ses zones d'intervention</p>
        </div>
      )}
    </div>
  );
};

export default LotsSelector;
